import React, { useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './pill-toggle.module.css';

/**
 * PillToggle
 *
 * Props:
 *   options  — Array<{ value: string, label: string }>  The selectable options.
 *   value    — string  The currently selected value.
 *   onChange — (value: string) => void
 *
 * Usage:
 *   <PillToggle
 *     options={[{ value: 'week', label: 'Last week' }, { value: 'month', label: 'Last month' }]}
 *     value={toggle}
 *     onChange={setToggle}
 *   />
 */
export const PillToggle = ({ options = [], value, onChange = () => {} }) => {
  const trackRef = useRef(null);
  const btnRefs = useRef({});
  const [sliderStyle, setSliderStyle] = useState({ width: 0, transform: 'translateX(0px)' });
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const activeBtn = btnRefs.current[value];
    const track = trackRef.current;
    if (!activeBtn || !track) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    setSliderStyle({
      width: btnRect.width,
      transform: `translateX(${btnRect.left - trackRect.left - 3}px)`
    });
    setReady(true);
  }, [value]);

  return (
    <div ref={trackRef} className={styles.track} role="group">
      {ready && <span className={styles.slider} style={sliderStyle} aria-hidden />}
      {options.map((opt) => (
        <button
          key={opt.value}
          ref={(el) => {
            btnRefs.current[opt.value] = el;
          }}
          type="button"
          className={cx(styles.btn, { [styles.active]: value === opt.value })}
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};
